import hoistNonReactStatics from 'hoist-non-react-statics';
import reactUpdate, {Spec} from 'immutability-helper';
import React from 'react';
import isEqual from 'react-fast-compare';
import {Unsubscribe} from 'redux';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {Selector} from 'notebook/ELNConstants';
import {PerformSingleInputTableOpFunc, StoreDependencyMap} from 'notebook/mediocre/EditorTypes';
import {MetadataForRestore} from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {UnknownNoteLinkedGroup} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {InitialCellValue} from 'notebook/structured-tables/util/getInitialCellValueFromLiveValue';
import excess from 'notebook/tables/excess';
import {FullTableMap, OutputTableMap} from 'notebook/tables/excess/Evaluator';
import type {ReorderOptions} from 'notebook/tables/excess/ops/TableOps';
import * as SpreadsheetEvalUtil from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import SpreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';
import {STRING_RESOLUTION_INITIAL_STATE} from 'notebook/tables/string-resolution/reduceStringResolution';
import {
  CellColors,
  Cells,
  InsertableColumnInfo,
  MultiSelectionBox,
  Table,
  TableIndex,
  TableOperation,
  TypedValue,
} from 'notebook/tables/TableConstants';
import {MODELS_INITIAL_STATE} from 'redux-stores/reduceModels';
import {getStore} from 'redux-stores/Store';
import StorableStore from 'samples/StorableStore';
import {WORKFLOWS_INITIAL_STATE} from 'workflows/reduceWorkflows';

import {NoteRuntimeSnapshot} from '../TableStoreTransforms';

// TODO(TypeScript): Remove `any` from these types.
export interface ExternalProps<TItem = unknown> {
  extraUsedTableNames?: () => Array<string>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getAllStoreDependenciesFromSpreadsheetRoot?: (spreadsheetRoot: any) => StoreDependencyMap;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getContent?: (item: any) => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getItems?: (root: any) => Array<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRoot?: () => any;
  getSpreadsheetEvalCacheKey?: () => string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getSpreadsheetTableName?: (item: any) => string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getType?: (item: any) => string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getCid?: (item: any) => string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  itemsAccessor?: (op: {[i: number]: Spec<any>}) => Spec<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  makeFullTableMap: (props: any) => FullTableMap;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectorsFromProps: (props: any) => Array<Selector>;
  setContent?: (type: string, content: object) => Spec<TItem>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  spreadsheetRoot: (props: any) => any;
  spreadsheetTypes?: Array<string>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateRoot?: (root: any) => void;
  shouldDisableFormulas?: () => boolean;
  requestSelection?: (selectionRestoreData: MetadataForRestore<TableIndex>) => void;
  tempTableStepUpdateHandler?: (fullTable: Table) => void;
  tempTableStepResetHandler?: (tableName: string) => void;
  // A null NoteRuntimeSnapshot can be specified if formulas are disabled.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getNoteRuntimeSnapshot: (props: any) => NoteRuntimeSnapshot | null;

  // HACK: This prop only exists to work around spreadsheet evaluation gaps in the mixture ingredients table.
  // It should not be used in typical usage. See BNCH-20069.
  disallowCellRefs?: boolean;

  // Used by file specific store to re-evaluate on demand
  fileId?: string;
  /** If the SpreadsheetEvaluation is for within a NoteLinkedGroup, the following prop is set */
  group?: UnknownNoteLinkedGroup;
}

export interface ComponentProps<T = unknown> extends ExternalProps<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setRef?: (element: any) => void;
}

export interface EnhancedProps extends State {
  evaluateTables: (
    fullTableMap: FullTableMap,
    selectors: Array<Selector>,
    noteRuntimeSnapshot: NoteRuntimeSnapshot | null
  ) => {computedTables: OutputTableMap; selectorCounts: {[selectorId: number]: number}};
  setMultiEditingForTable: (
    tableName: string,
    {
      editing,
      text,
      typedValue,
      selection,
    }: {
      editing: boolean;
      text: string;
      typedValue: TypedValue;
      selection: {cid: string; start: number; end: number} | null;
    }
  ) => void;
  lookUpTableNameWithActiveEdit: () => string | null;
  tryInsert: (
    tableName: string,
    e: {index: [number, number]; start: number; end: number; cid: string; shift: boolean}
  ) => boolean;
  canInsert: (e: {start: number; end: number; cid: string; shift: boolean}) => boolean;
  disallowMultiExpand: () => boolean;
  deleteTableRows: (index: number, start: number, end: number) => void;
  deleteTableRowsNonContiguous: (index: number, ranges: Array<[number, number]>) => void;
  deleteTableColumns: (index: number, start: number, end: number) => void;
  insertTableRowsAbove: (index: number, start: number, end: number) => void;
  insertTableRowsBelow: (index: number, start: number, end: number) => void;
  reorderTableRowsOrColumns: (index: number, options: ReorderOptions) => void;
  insertTableColumnsLeft: (
    index: number,
    start: number,
    end: number,
    columnInfo: InsertableColumnInfo | {}
  ) => void;
  insertTableColumnsRight: (
    index: number,
    start: number,
    end: number,
    columnInfo: InsertableColumnInfo | {}
  ) => void;
  renameTable: (index: number, newTableName: string) => void;
  mergeTableRegion: (index: number, selectedBox: MultiSelectionBox) => void;
  unmergeTableRegion: (index: number, selectedBox: MultiSelectionBox) => void;
  mergeIntoSingleInput: PerformSingleInputTableOpFunc;
  unmergeSingleInput: PerformSingleInputTableOpFunc;
  isTableNameUsed: (tableName: string) => boolean;
  updateSelectors: (newSelectors: Array<Selector>) => void;
  usedTableNames: () => Array<string>;
  performTableOp: (tableOperation: TableOperation) => void;
}

interface State {
  fullTableMap: FullTableMap;
  tempFullTableMap: FullTableMap;
  computedTables: OutputTableMap;
  cellColors: {[tableName: string]: CellColors};
  editingTable: string | null;
  showExcessLabels: boolean;
  tableNamespace: string;
  selectorCounts: {[selectorId: number]: number};
}

export default function withSpreadsheetEvaluation<P extends ComponentProps<T>, T = unknown>(
  WrappedComponent: React.ComponentClass<P & EnhancedProps>
): React.ComponentClass<P> {
  class Component extends React.Component<P, State> {
    _storeDependencies: StoreDependencyMap;

    _storeState: SpreadsheetEvalUtil.SpreadsheetEvalStoreState;

    _reduxUnsubscribe: Unsubscribe | null;

    constructor(props: P) {
      super(props);
      this._storeState = {
        elnResolution: STRING_RESOLUTION_INITIAL_STATE.resolutionResultsByResolutionQuery,
        workflows: WORKFLOWS_INITIAL_STATE,
        fileSummariesById: MODELS_INITIAL_STATE.fileSummariesById,
        storablesById: {},
        storableSummariesById: {},
      };
      this._storeDependencies = {};
      this._reduxUnsubscribe = null;
      const tableNamespace = _.uniqueId('table-ns');
      // eslint-disable-next-line no-restricted-syntax
      this.state = _.extend({}, this._spreadsheetStoreState(tableNamespace), {
        tableNamespace,
      });
    }

    _getNoteRuntimeSnapshotIfEnabled(this: void, props: P): NoteRuntimeSnapshot | null {
      if (!BenchlingConfig.ELN_ENABLE_RUNTIME_SNAPSHOT_RENDER) {
        return null;
      }
      return props.getNoteRuntimeSnapshot(props);
    }

    UNSAFE_componentWillMount(): void {
      const fullTableMap = this.props.makeFullTableMap(this.props);
      const selectors = this.props.selectorsFromProps(this.props);
      const noteRuntimeSnapshot = this._getNoteRuntimeSnapshotIfEnabled(this.props);
      this.setState({tempFullTableMap: fullTableMap});
      this._updateEvaluatedTableState(fullTableMap, selectors, noteRuntimeSnapshot);
      this.setState({fullTableMap});
    }

    componentDidMount(): void {
      this._storeDependencies = this.getAllStoreDependencies(this.props);
      // Force an update whenever anything in the ELN redux tree updates (resolution results or
      // resolution loading state), when the stage runs for the entry are changed,
      // or when the bioentities with ids in this._storeDependencies update in the redux store.
      this._reduxUnsubscribe = getStore().subscribe(this._updateIfStoreDependenciesChanged);
      // Listen to storable IDs needed by transfer table custom eval.
      // The storable IDs are computed by getStoreDependenciesForTransferTable
      StorableStore.addChangeListener(this._updateIfStoreDependenciesChanged);
      SpreadsheetStore.addChangeListener(this._spreadsheetStoreChangeHandler);
    }

    componentWillUnmount(): void {
      if (this._reduxUnsubscribe) {
        this._reduxUnsubscribe();
      }
      StorableStore.removeChangeListener(this._updateIfStoreDependenciesChanged);
      SpreadsheetStore.removeChangeListener(this._spreadsheetStoreChangeHandler);
    }

    UNSAFE_componentWillReceiveProps(nextProps: P): void {
      // Determine if we should trigger table evaluation based on new props.

      const {noteRuntimeSnapshot, selectors, fullTableMap, shortCircuit} =
        getDataForUpdatingEvaluatedTableState(
          this.props as ComponentProps<unknown>,
          this.state,
          nextProps,
          this._getNoteRuntimeSnapshotIfEnabled
        );
      if (shortCircuit) {
        return;
      }

      // Run table evaluation and update component state. If persistEval is enabled,
      // "evaluation" will just mean grabbing the new evaluationSnapshot from the NRS
      this._storeDependencies = this.getAllStoreDependencies(nextProps);
      this.setState({tempFullTableMap: fullTableMap!});
      this._updateEvaluatedTableState(fullTableMap!, selectors, noteRuntimeSnapshot);
      this.setState({fullTableMap: fullTableMap!});
    }

    _handleChangeTempFullTableMap = (newTempFullTableMap: FullTableMap): void => {
      this.setState({tempFullTableMap: newTempFullTableMap});
    };

    _updateIfStoreDependenciesChanged = (): void => {
      // If an evaluation snapshot exists then the entry has been sent for review and we do not want to rerun
      // evaluation which could potentially cause the entry to change
      if (this._getNoteRuntimeSnapshotIfEnabled(this.props)?.evaluationSnapshot != null) {
        return;
      }

      this._storeState = SpreadsheetEvalUtil.updateIfStoreDependenciesChanged({
        storeState: this._storeState,
        storeDependencies: this._storeDependencies,
        tempFullTableMap: this.state.tempFullTableMap,
        handleChangeTempFullTableMap: this._handleChangeTempFullTableMap,
        makeFullTableMap: () => this.props.makeFullTableMap(this.props),
        selectorsFromProps: () => this.props.selectorsFromProps(this.props),
        updateEvaluatedTableState: this._updateEvaluatedTableState,
        fileId: this.props.fileId ?? null,
        isNoteLinkedGroup: Boolean(this.props.group),
      });
    };

    _hasFormulasDisabled(): boolean {
      return this.props.shouldDisableFormulas == null ? false : this.props.shouldDisableFormulas();
    }

    evaluateTables = (
      fullTableMap: FullTableMap,
      selectors: Array<Selector>,
      noteRuntimeSnapshot: NoteRuntimeSnapshot | null
    ): {computedTables: OutputTableMap; selectorCounts: {[selectorId: number]: number}} => {
      if (
        BenchlingConfig.ELN_ENABLE_RUNTIME_SNAPSHOT_RENDER &&
        noteRuntimeSnapshot &&
        noteRuntimeSnapshot.evaluationSnapshot
      ) {
        if (window.debug) {
          // eslint-disable-next-line no-console
          console.info('[EXCESS] Rendering evaluation from snapshot', noteRuntimeSnapshot);
        }
        // While we maintain both persisted and non-persisted evaluation, we copy the data out of the
        // NoteRuntimeSnapshot into component state.
        return {
          computedTables: noteRuntimeSnapshot.evaluationSnapshot,
          selectorCounts: SpreadsheetEvalUtil.getSelectorCounts(fullTableMap),
        };
      } else {
        return SpreadsheetEvalUtil.evaluateTables({
          fullTableMap,
          selectors,
          oldComputedTables: this.state.computedTables,
          hasFormulasDisabled: this._hasFormulasDisabled(),
          spreadsheetEvalCacheKey: this.props.getSpreadsheetEvalCacheKey
            ? this.props.getSpreadsheetEvalCacheKey()
            : null,
          disallowCellRefs: this.props.disallowCellRefs,
          fileId: this.props.fileId ?? null,
        });
      }
    };

    _updateEvaluatedTableState = (
      fullTableMap: FullTableMap,
      selectors: Array<Selector>,
      noteRuntimeSnapshot: NoteRuntimeSnapshot | null
    ): void => {
      const {computedTables, selectorCounts} = this.evaluateTables(
        fullTableMap,
        selectors,
        noteRuntimeSnapshot
      );
      this.setState({computedTables, selectorCounts});
    };

    _spreadsheetStoreState(tableNamespace: string): {
      cellColors: EnhancedProps['cellColors'];
      editingTable: string | null;
      showExcessLabels: boolean;
    } {
      return SpreadsheetEvalUtil.getSpreadsheetStoreState(tableNamespace, this._hasFormulasDisabled());
    }

    _spreadsheetStoreChangeHandler = (): void => {
      this.setState(this._spreadsheetStoreState(this.state.tableNamespace));
    };

    setMultiEditingForTable = (
      tableName: string,
      {
        editing,
        text,
        typedValue,
        selection,
      }: {
        editing: boolean;
        text: string;
        typedValue: TypedValue;
        selection: {cid: string; start: number; end: number} | null;
      }
    ): void => {
      SpreadsheetEvalUtil.setMultiEditingForTable(tableName, this.state.tableNamespace, {
        editing,
        text,
        typedValue,
        selection,
      });
    };

    // Wrap @state.editingTable in a method to make it clear that should not be accessed inside
    // a render method, but instead can be accessed while handling an event.
    lookUpTableNameWithActiveEdit = (): string | null => this.state.editingTable;

    tryInsert = (
      tableName: string,
      e: {index: [number, number]; start: number; end: number; cid: string; shift: boolean}
    ): boolean =>
      SpreadsheetEvalUtil.tryInsert({
        tableName,
        e,
        hasFormulasDisabled: this._hasFormulasDisabled(),
        tableNamespace: this.state.tableNamespace,
        editingTable: this.state.editingTable,
      });

    canInsert = (e: {start: number; end: number; cid: string; shift: boolean}): boolean =>
      SpreadsheetEvalUtil.canInsert({
        e,
        hasFormulasDisabled: this._hasFormulasDisabled(),
        tableNamespace: this.state.tableNamespace,
        editingTable: this.state.editingTable,
      });

    /**
     * Performs an operation on a table, updating the appropriate react components
     * @param  {Number} index   The index of the targetItem containing the table with targetTableName
     * @param  {Function} op    (originalTableMap, targetTableName) -> {tables: newTableMap, box}, where
     *                            originalTableMap {Object}  Maps original names of tables to original tables.
     *                            targetTableName {String}   The name of the table being directly modified
     *                            newTableMap {Object}       Maps *original* names of tables to new tables.
     *                            box {Object}               {minI, maxI, minJ, maxJ}, or null if no selection
     *                                                       is specified
     */
    _performTableOp = ({index, op, options}: TableOperation): void => {
      // Many of these props are not specified for protocol viewers, which do not perform
      // table operations. However, making this typecheck without failing silently is difficult
      // since assertions do not affect typechecking.
      SpreadsheetEvalUtil.performTableOp({
        index,
        op,
        options,
        spreadsheetTypes: this.props.spreadsheetTypes!,
        computedTables: this.state.computedTables,
        getItems: this.props.getItems!,
        getRoot: this.props.getRoot!,
        getType: this.props.getType!,
        getCid: this.props.getCid!,
        getContent: this.props.getContent!,
        setContent: this.props.setContent!,
        requestSelection: this.props.requestSelection!,
        updateRoot: this.props.updateRoot!,
        itemsAccessor: this.props.itemsAccessor!,
      });
    };

    // We disable expanding a multiselect via drag while the a table is actively being edited,
    // as this triggers the child table to clear its selection (via multiMouseLeaveHandler), however,
    // the table is still identified by state.editingTable. We let the table keep its selection so that
    // it can handle finalizing the edit.
    disallowMultiExpand = (): boolean => this.state.editingTable != null;

    deleteTableRowsNonContiguous = (index: number, ranges: Array<[number, number]>): void => {
      if (assertAndContinue(ranges.length > 0, 'Cannot delete rows with empty ranges')) {
        this._performTableOp({
          index,
          op: (tableMap, _, targetTableName) =>
            excess.ops.deleteRowsNonContiguous(tableMap, targetTableName, ranges),
          options: {forRowRemoval: true, remakeSingleInputs: true},
        });
      }
    };

    deleteTableRows = (index: number, start: number, end: number): void => {
      if (end == null) {
        end = start;
      }
      this._performTableOp({
        index,
        op: (tableMap, computedTables, targetTableName) =>
          excess.ops.deleteRows(tableMap, targetTableName, start, end),
        options: {forRowRemoval: true, remakeSingleInputs: true},
      });
    };

    deleteTableColumns = (index: number, start: number, end: number): void => {
      if (end == null) {
        end = start;
      }
      this._performTableOp({
        index,
        op: (tableMap, computedTables, targetTableName) =>
          excess.ops.deleteColumns(tableMap, targetTableName, start, end),
        options: {forColumnEdit: true},
      });
    };

    insertTableRowsAbove = (index: number, start: number, end: number): void => {
      if (end == null) {
        end = start;
      }
      this._performTableOp({
        index,
        op: (tableMap, computedTables, targetTableName) =>
          excess.ops.insertRowsAbove(tableMap, targetTableName, start, end),
        options: {remakeSingleInputs: true},
      });
    };

    insertTableRowsBelow = (index: number, start: number, end: number): void => {
      if (end == null) {
        end = start;
      }
      this._performTableOp({
        index,
        op: (tableMap, computedTables, targetTableName) =>
          excess.ops.insertRowsBelow(tableMap, targetTableName, start, end),
        options: {remakeSingleInputs: true},
      });
    };

    reorderTableRowsOrColumns = (index: number, options: ReorderOptions): void => {
      this._performTableOp({
        index,
        op: (tableMap, computedTables, targetTableName) =>
          excess.ops.reorderRowsOrColumns(tableMap, targetTableName, options),
        options: {remakeSingleInputs: true},
      });
    };

    insertTableColumnsLeft = (
      index: number,
      start: number,
      end: number,
      columnInfo: InsertableColumnInfo | {} = {},
      initialCellValues?: Array<InitialCellValue>
    ): void => {
      if (end == null) {
        end = start;
      }
      this._performTableOp({
        index,
        op: (tableMap, computedTables, targetTableName) =>
          excess.ops.insertColumnsLeft(tableMap, targetTableName, start, end, columnInfo, initialCellValues),
        options: {forColumnEdit: true, remakeSingleInputs: true},
      });
    };

    insertTableColumnsRight = (
      index: number,
      start: number,
      end: number,
      columnInfo: InsertableColumnInfo | {} = {},
      initialCellValues?: Array<InitialCellValue>
    ): void => {
      if (end == null) {
        end = start;
      }
      this._performTableOp({
        index,
        op: (tableMap, computedTables, targetTableName) =>
          excess.ops.insertColumnsRight(tableMap, targetTableName, start, end, columnInfo, initialCellValues),
        options: {forColumnEdit: true, remakeSingleInputs: true},
      });
    };

    renameTable = (index: number, newTableName: string): void => {
      this._performTableOp({
        index,
        op: (tableMap, computedTable, targetTableName) =>
          excess.ops.renameTable(tableMap, targetTableName, newTableName),
      });
    };

    mergeTableRegion = (index: number, selectedBox: MultiSelectionBox): void => {
      this._performTableOp({
        index,
        op: (tableMap, computedTables, targetTableName) =>
          excess.ops.mergeRegion(tableMap, computedTables, targetTableName, selectedBox),
      });
    };

    unmergeTableRegion = (index: number, selectedBox: MultiSelectionBox): void => {
      this._performTableOp({
        index,
        op: (tableMap, computedTables, targetTableName) =>
          excess.ops.unmergeRegion(tableMap, computedTables, targetTableName, selectedBox),
      });
    };

    mergeIntoSingleInput = (index: number, startIndex: number, endIndex: number): void => {
      this._performTableOp({
        index,
        op: (tableMap, computedTables, targetTableName) =>
          excess.ops.mergeIntoSingleInput(tableMap, computedTables, targetTableName, startIndex, endIndex),
        options: {remakeSingleInputs: false},
      });
    };

    unmergeSingleInput = (index: number, startIndex: number, endIndex: number): void => {
      this._performTableOp({
        index,
        op: (tableMap, computedTables, targetTableName) =>
          excess.ops.unmergeSingleInput(tableMap, computedTables, targetTableName, startIndex, endIndex),
        options: {remakeSingleInputs: false},
      });
    };

    // Determines whether tableName is used anywhere within the existing note.
    isTableNameUsed = (tableName: string): boolean => this.usedTableNames().includes(tableName);

    getAllStoreDependencies(props: P): StoreDependencyMap {
      // Not all users of this HOC pass `getAllStoreDependenciesFromSpreadsheetRoot`.
      if (!this.props.getAllStoreDependenciesFromSpreadsheetRoot) {
        return {};
      }
      return this.props.getAllStoreDependenciesFromSpreadsheetRoot(this.props.spreadsheetRoot(props));
    }

    updateSelectors = (newSelectors: Array<Selector>): void => {
      const root = reactUpdate(this.props.getRoot!(), {$merge: {selectors: newSelectors}});
      this.props.updateRoot!(root);
    };

    // Utility methods

    usedTableNames = (): Array<string> => {
      // eslint-disable-next-line no-restricted-syntax
      let usedTableNames: Array<string> = _.keys(this.state.fullTableMap);
      if (typeof this.props.extraUsedTableNames === 'function') {
        usedTableNames = [...usedTableNames, ...this.props.extraUsedTableNames()];
      }
      return usedTableNames;
    };

    tempTableStepUpdateHandler = (fullTable: Table): void => {
      const {table} = this.state.tempFullTableMap[fullTable.name];
      if (fullTable.table !== table) {
        this._setTableOverride(fullTable.name, fullTable.table);
      }
    };

    tempTableStepResetHandler = (name: string): void => {
      const newTable = this.state.fullTableMap[name].table;
      this._setTableOverride(name, newTable);
    };

    _setTableOverride(tableName: string, table: Cells): void {
      const tableUpdateSpec = {$merge: {table}};
      const tempFullTableMap = reactUpdate(this.state.tempFullTableMap, {[tableName]: tableUpdateSpec});
      const selectors = this.props.selectorsFromProps(this.props);
      this.setState({tempFullTableMap});
      // Table overrides are only used by protocols (which only have plain tables) don't want to use
      // a persisted noteRuntimeSnapshot anyhow.
      this._updateEvaluatedTableState(tempFullTableMap, selectors, null);
    }

    render(): JSX.Element {
      return (
        <WrappedComponent
          {...this.props}
          {...this.state}
          ref={this.props.setRef}
          evaluateTables={this.evaluateTables}
          setMultiEditingForTable={this.setMultiEditingForTable}
          lookUpTableNameWithActiveEdit={this.lookUpTableNameWithActiveEdit}
          tryInsert={this.tryInsert}
          canInsert={this.canInsert}
          disallowMultiExpand={this.disallowMultiExpand}
          deleteTableRows={this.deleteTableRows}
          deleteTableRowsNonContiguous={this.deleteTableRowsNonContiguous}
          deleteTableColumns={this.deleteTableColumns}
          insertTableRowsAbove={this.insertTableRowsAbove}
          insertTableRowsBelow={this.insertTableRowsBelow}
          reorderTableRowsOrColumns={this.reorderTableRowsOrColumns}
          insertTableColumnsLeft={this.insertTableColumnsLeft}
          insertTableColumnsRight={this.insertTableColumnsRight}
          renameTable={this.renameTable}
          performTableOp={this._performTableOp}
          mergeTableRegion={this.mergeTableRegion}
          unmergeTableRegion={this.unmergeTableRegion}
          mergeIntoSingleInput={this.mergeIntoSingleInput}
          unmergeSingleInput={this.unmergeSingleInput}
          updateSelectors={this.updateSelectors}
          usedTableNames={this.usedTableNames}
          isTableNameUsed={this.isTableNameUsed}
          tempTableStepUpdateHandler={this.tempTableStepUpdateHandler}
          tempTableStepResetHandler={this.tempTableStepResetHandler}
        />
      );
    }
  }

  // As we're wrapping the component, we need to move static fields
  // from the component we're wrapping into the provided component.
  // This is needed for things like SimpleTableEditable.standaloneProps.
  hoistNonReactStatics(Component, WrappedComponent);
  return Component;
}

/**
 * Assembles the data needed to do table evaluation while determining whether or not evaluation needs to
 * be performed.
 * @param props the current component props
 * @param state the current state
 * @param nextProps the next component props
 * @param getNoteRuntimeSnapshot method to retrieve the NoteRuntimeSnapshot from props if enabled
 * @returns data needed for evaluation. If shortCircuit is false, fullTableMap will be non-null.
 */
function getDataForUpdatingEvaluatedTableState(
  props: ComponentProps<unknown>,
  state: State,
  nextProps: ComponentProps<unknown>,
  getNoteRuntimeSnapshot: (props: ComponentProps<unknown>) => NoteRuntimeSnapshot | null
): {
  noteRuntimeSnapshot: NoteRuntimeSnapshot | null;
  selectors: Array<Selector>;
  fullTableMap: FullTableMap | null;
  shortCircuit: boolean;
} {
  const result = {
    noteRuntimeSnapshot: getNoteRuntimeSnapshot(nextProps),
    selectors: props.selectorsFromProps(nextProps),
    fullTableMap: null,
    shortCircuit: false,
  };

  // Test 1: If NoteRuntimeSnapshot is not null (persist eval is enabled for this doc type),
  // we can short circuit if current and next NRSs are referentially equal.
  if (result.noteRuntimeSnapshot != null && result.noteRuntimeSnapshot === getNoteRuntimeSnapshot(props)) {
    return {...result, shortCircuit: true};
  }

  let nextFullTableMap: FullTableMap;
  if (result.noteRuntimeSnapshot == null) {
    // Test 2: If NRS is null (persist eval not enabled), we can short circuit if
    // current and next spreadsheet roots and selectors are referentially equal.
    if (
      props.spreadsheetRoot(nextProps) === props.spreadsheetRoot(props) &&
      result.selectors === props.selectorsFromProps(props)
    ) {
      return {...result, shortCircuit: true};
    }

    // Test 3: If NRS is null (persist eval not enabled), we can avoid full evaluation if
    // we generate a new fullTableMap and it's deep-equal to the current fullTableMap in state.
    // Checking table deep quality is somewhat expensive as entries get large, which is why we try to
    // short circuit with quicker checks above.
    nextFullTableMap = props.makeFullTableMap(nextProps);
    if (
      result.selectors === props.selectorsFromProps(props) &&
      isEqual(state.fullTableMap, nextFullTableMap)
    ) {
      return {...result, shortCircuit: true};
    }
  } else {
    nextFullTableMap = props.makeFullTableMap(nextProps);
  }

  return {...result, fullTableMap: nextFullTableMap};
}

export const _forTesting = {
  getDataForUpdatingEvaluatedTableState,
};
